// A return visit on an existing job (gap #5: appointments.add): after a tech pauses with "Needs
// parts" or "Return visit", the office books the next visit on the same job instead of a new one.
// Days count from the company's today; a window already started today isn't offered. The visit
// starts scheduled (the board dispatches it); the server says why when it refuses (a completed job).
// For those who dispatch (jobs.dispatch); Review and the board's visit panel use it.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { sentences } from '@flowology/kernel';
import { client } from '../api.ts';
import {
  DAY_WINDOWS,
  dayOptions,
  hourNow,
  today as todayIn,
  windowLabel,
  windowStarted,
  zonedInstant,
} from '../book-model.ts';
import { SaveButton, useSaveKey } from './SaveButton.tsx';

export function AddVisit({
  jobId,
  jobNumber,
  leadTechId,
  timeZone,
  onAdded,
}: {
  jobId: string;
  jobNumber: string;
  /** The tech who had the job, offered first. */
  leadTechId: string | null;
  timeZone: string;
  /** Said once the visit is booked ("Return visit on Tue, Oct 20, 8–10 a.m. …"). */
  onAdded: (said: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [techs, setTechs] = useState<{ id: string; name: string }[]>([]);
  const today = todayIn(timeZone);
  const nowH = hourNow(timeZone);
  const days = dayOptions(today, 14);
  const firstWindow = (day: string) => {
    const i = DAY_WINDOWS.findIndex((w) => !windowStarted(day, today, w, nowH));
    return i < 0 ? 0 : i;
  };
  const opens = DAY_WINDOWS.every((w) => windowStarted(today, today, w, nowH)) ? days[1]!.date : today;
  const [day, setDay] = useState(opens);
  const [win, setWin] = useState(() => firstWindow(opens));
  const [tech, setTech] = useState(leadTechId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(JSON.stringify([jobId, day, win, tech]));
  useEffect(() => {
    if (!open || techs.length) return;
    void client
      .call<{ staff: { id: string; displayName: string }[] }>('staff.list', { techniciansOnly: true })
      .then((r) => setTechs(r.staff.map((s) => ({ id: s.id, name: s.displayName }))))
      .catch(() => setTechs([]));
  }, [open, techs.length]);

  if (!open)
    return (
      <button className="btn soft" onClick={() => setOpen(true)}>
        Add a return visit
      </button>
    );
  const started = windowStarted(day, today, DAY_WINDOWS[win]!, nowH);
  const add = async () => {
    const [s0, e0] = DAY_WINDOWS[win]!;
    const start = zonedInstant(day, s0, timeZone);
    const end = zonedInstant(day, e0, timeZone);
    setBusy(true);
    setError(null);
    try {
      await client.call(
        'appointments.add',
        {
          jobId,
          start,
          end,
          arrivalWindow: { start, end },
          techIds: tech ? [tech] : [],
          ...(tech ? { leadTechId: tech } : {}),
        },
        { idempotencyKey: save.key },
      );
      save.renew();
      setOpen(false);
      const label = days.find((d) => d.date === day)?.label ?? day;
      onAdded(
        sentences(
          `Return visit on #${jobNumber}: ${label === 'Today' || label === 'Tomorrow' ? label.toLowerCase() : label}, ${windowLabel(DAY_WINDOWS[win]!)}`,
          'Dispatch it from the board on the day',
        ),
      );
    } catch (e) {
      setError(
        e instanceof RpcError ? e.message : 'Couldn’t add the visit. Try again; it won’t be added twice.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="move add-visit" role="group" aria-label={`Return visit on #${jobNumber}`}>
      <label>
        Day
        <select
          className="field"
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            setWin(firstWindow(e.target.value));
          }}
        >
          {days.map((d) => (
            <option key={d.date} value={d.date}>
              {d.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Arrival window
        <select className="field" value={win} onChange={(e) => setWin(Number(e.target.value))}>
          {DAY_WINDOWS.map((w, i) => (
            <option key={i} value={i} disabled={windowStarted(day, today, w, nowH)}>
              {windowLabel(w)}
              {windowStarted(day, today, w, nowH) ? ' (already started)' : ''}
            </option>
          ))}
        </select>
      </label>
      <label>
        Technician
        <select className="field" value={tech} onChange={(e) => setTech(e.target.value)}>
          <option value="">Choose on the board</option>
          {techs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="row">
        <button className="btn soft" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <SaveButton
          busy={busy}
          busyLabel="Adding…"
          error={error}
          disabled={started}
          onClick={() => void add()}
        >
          Add the visit
        </SaveButton>
      </div>
    </div>
  );
}
